import { BefehlFormAuftrag, BefehlFormField, ReadOnlyText } from '../Generic'
import { useTranslation } from 'react-i18next'
import {
  BetriebsstelleField,
  BetriebsstellenartField,
  RadioChipGroup,
  RadioChipOption
} from '../../form'
import { useFormContext } from 'react-hook-form'
import { useEffect } from 'react'
import { KmZeilenForm } from './KmZeilenForm'
import BorderedBox from '../Generic/BorderedBox'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'

export interface Auftrag8_50FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
}

export function Auftrag8_50Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag8_50FormProps>) {
  const formContext = useFormContext()
  const { control, formState, watch, setValue } = formContext
  const { errors } = formState
  const { t } = useTranslation('ui-befehle')

  const selector = watch('auftrag_8_50.selector')
  const x43Betriebsstellenart = watch('auftrag_8_50.field_x_43.art')
  const x44Betriebsstellenart = watch('auftrag_8_50.field_x_44.art')

  useEffect(() => {
    if (selector === '') {
      setValue('auftrag_8_50.field_x_43.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
      setValue('auftrag_8_50.field_x_44.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
    } else if (selector === 'in') {
      setValue('auftrag_8_50.field_x_44.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
    }
  }, [selector, setValue])

  return (
    <BefehlFormAuftrag
      auftragKennung="8.50"
      fieldName="auftrag_8_50.enabled"
      readOnly={true}
      disabled={false}
      testId={testId}
    >
      <div className="flex items-baseline gap-0.5 flex-wrap">
        <span data-testid={`${testId}-text`}>{t('auftraege.8_50.label')}</span>
      </div>
      <div className="flex-1">
        <div className="w-full">
          <div className="flex flex-col gap-1">
            <div className="@container flex flex-col">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 @[400px]:text-right flex @[400px]:justify-end items-baseline">
                  <BefehlFormField
                    name="auftrag_8_50.selector"
                    control={control}
                    label=""
                    readOnly={readOnly}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-8-43-44-selector`}
                    render={({ field, isDisabled }) => {
                      const options: RadioChipOption[] = [
                        {
                          value: 'zwischen',
                          label: t('auftraege.8_50.fields.zwischen_label')
                        },
                        {
                          value: 'in',
                          label: t('auftraege.8_50.fields.in_label')
                        }
                      ]
                      return (
                        <RadioChipGroup
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          options={options}
                          disabled={isDisabled}
                          readOnly={readOnly}
                          showOnlySelected={readOnly}
                          testId={`${testId}-feld-8-43-44-selector-inner`}
                        />
                      )
                    }}
                  />
                </div>
                <div className="flex-1">
                  {selector === '' ? (
                    <div className="border border-cool-gray-200 px-1 px-1 py-1">
                      <ReadOnlyText
                        value={
                          readOnly
                            ? ''
                            : t(
                                'auftraege.8_50.fields.placeholder_select_in_zwischen'
                              )
                        }
                        label=""
                        variant="solid"
                        activateEmptyWarning={activateEmptyFieldWarnings}
                        data-testid={`${testId}-feld-8-43-44-placeholder`}
                      />
                    </div>
                  ) : (
                    <BorderedBox
                      footer={
                        <span data-testid={`${testId}-feld-8-43-label`}>
                          {t('auftraege.8_50.fields.x_43_label')}
                        </span>
                      }
                      className={
                        selector === 'zwischen'
                          ? '@[400px]:border-b-0 @[400px]:rounded-b-none'
                          : ''
                      }
                      data-testid={`${testId}-feld-8-43-bordered-box`}
                    >
                      <ZugmeldestelleWrapper
                        betriebsstellenartField={
                          <BefehlFormField
                            name="auftrag_8_50.field_x_43.art"
                            control={control}
                            label="Betriebsstellenart"
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-feld-8-43-art`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstellenartField
                                {...field}
                                label={t(
                                  'auftraege.8_50.fields.betriebsstellenart_label'
                                )}
                                variant="solid"
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                data-testid={`${testId}-feld-8-43-art-inner`}
                                selectClassName="w-full"
                              />
                            )}
                          />
                        }
                        bestriebsstelleField={
                          <BefehlFormField
                            name="auftrag_8_50.field_x_43.name"
                            control={control}
                            label={t('auftraege.8_50.fields.zugmeldestelle')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-feld-8-43-name`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstelleField
                                {...field}
                                label={t(
                                  'auftraege.8_50.fields.zugmeldestelle'
                                )}
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                typ={x43Betriebsstellenart}
                                data-testid={`${testId}-feld-8-43-name-inner`}
                              />
                            )}
                          />
                        }
                      />
                    </BorderedBox>
                  )}
                </div>
              </div>

              <div
                className={
                  selector === 'zwischen'
                    ? '@container max-h-96 opacity-100'
                    : '@container max-h-0 opacity-0'
                }
              >
                <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                  <div className="mt-0.5 @[400px]:w-8 @[400px]:text-right">
                    <span data-testid={`${testId}-text-und`}>
                      {t('auftraege.8_50.fields.und_label')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <BorderedBox
                      data-testid={`${testId}-feld-8-44-bordered-box`}
                      className="@[400px]:border-t-0 @[400px]:rounded-t-none"
                      footer={
                        <span data-testid={`${testId}-feld-8-44-label`}>
                          {t('auftraege.8_50.fields.x_44_label')}
                        </span>
                      }
                    >
                      <ZugmeldestelleWrapper
                        betriebsstellenartField={
                          <BefehlFormField
                            name="auftrag_8_50.field_x_44.art"
                            control={control}
                            label="Betriebsstellenart"
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-feld-8-44-art`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstellenartField
                                {...field}
                                label={t(
                                  'auftraege.8_50.fields.betriebsstellenart_label'
                                )}
                                variant="solid"
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                data-testid={`${testId}-feld-8-44-art-inner`}
                                selectClassName="w-full"
                              />
                            )}
                          />
                        }
                        bestriebsstelleField={
                          <BefehlFormField
                            name="auftrag_8_50.field_x_44.name"
                            control={control}
                            label={t('auftraege.8_50.fields.zugmeldestelle')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-feld-8-44-name`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstelleField
                                {...field}
                                label={t(
                                  'auftraege.8_50.fields.zugmeldestelle'
                                )}
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                typ={x44Betriebsstellenart}
                                data-testid={`${testId}-feld-8-44-name-inner`}
                              />
                            )}
                          />
                        }
                      />
                    </BorderedBox>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full">
              <KmZeilenForm
                readOnly={readOnly}
                testId={testId}
                activateEmptyFieldWarnings={activateEmptyFieldWarnings}
              />
            </div>

            <div className="w-full">
              <span data-testid={`${testId}-text-und-darf-weiterfahren`}>
                {t('auftraege.8_50.und_darf_weiterfahren')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </BefehlFormAuftrag>
  )
}
